import { useMemo } from 'react';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { useEnv } from '@zenbusiness/application-commons-env';
import { useIdentity } from '@zenbusiness/application-commons-identity';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import BankingChart from '#client/components/BankingChart';
import BankingTitle from '#client/components/BankingTitle';
import { Breadcrumbs } from '#client/components/Breadcrumbs';
import { DisableLayer } from '#client/components/DisableLayer';
import FundboxFuseWidget from '#client/components/FundboxFuseWidget';
import { HubContainerGridStyled, FocusGrid } from '#client/components/Grid';
import Notifications from '#client/components/Notifications';
import ActivateCardBanner from '#client/components/PageBanners/ActivateCardBanner';
import AdditionalInfoBanner from '#client/components/PageBanners/AdditionalInfoBanner';
import ApplicationDocsStatusBanner from '#client/components/PageBanners/ApplicationDocsStatusBanner';
import ApplicationStatusBanner from '#client/components/PageBanners/ApplicationStatusBanner';
import BankingTransitionBanner from '#client/components/PageBanners/BankingTransitionBanner';
import CancelSubscriptionBanner from '#client/components/PageBanners/CancelSubscriptionBanner';
import FrozenAccountBanner from '#client/components/PageBanners/FrozenAccountBanner';
import FrozenCardBanner from '#client/components/PageBanners/FrozenCardBanner';
import ServerDrivenBanner from '#client/components/PageBanners/ServerDrivenBanner';
import SuspectedFraudCardBanner from '#client/components/PageBanners/SuspectedFraudCardBanner';
import UnAuthenticatedBanner from '#client/components/PageBanners/UnAuthenticatedBanner';
import { DB_APPLICATION_STATUS } from '#client/constants/application';
import {
  ENABLE_FUNDBOX_QUALIFIED_LIST_CONTROL_WIDGET,
  ZB_BANKING_SUNSET_PH3
} from '#client/constants/experimentsFlags';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useLendingProspects from '#client/hooks/useLendingProspects';
import { useQueryParamModal } from '#client/hooks/useQueryParamModal';

import { BreadcrumbWrapStyled } from '../SharedStyles';
import BalanceBox from './components/BalanceBox';
import ConnectedAccounts from './components/ConnectedAccounts';
import FinanceTrends from './components/FinanceTrends';
import Footer from './components/Footer';
import NextUpCards from './components/NextUpCards';
import Transactions from './components/Transactions';
import WhatsNewBanner from './components/WhatsNewBanner';
import { DashboardWrapperStyled, LeftSideStyled, RightSideStyled } from './Dashboard.styles';

const PAGE_TYPE_MAP_APP_STATUS = {
  [DB_APPLICATION_STATUS.NOT_STARTED]: 'banking_dashboard_no_order_empty',
  [DB_APPLICATION_STATUS.PENDING]: 'pending_state_banking_dashboard',
  [DB_APPLICATION_STATUS.PENDING_REVIEW]: 'pending_state_banking_dashboard',
  [DB_APPLICATION_STATUS.APPROVED]: 'active_banking_dashboard',
  [DB_APPLICATION_STATUS.CLOSED]: 'closed_account_banking_dashboard',
  [DB_APPLICATION_STATUS.INCOMPLETE]: 'application_pending_banking_dashboard',
  [DB_APPLICATION_STATUS.STARTED]: 'application_pending_banking_dashboard',
  [DB_APPLICATION_STATUS.AWAITING_CHECKOUT]: 'application_pending_banking_dashboard'
};

const { isWebView } = WebView;

// Component for when feature flag is ON (show to everyone)
function FundboxFuseWidgetForAll({ partnerId }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { email, phone } = useIdentity();

  if (!phone || !email || !businessEntityUuid) {
    return null;
  }

  return (
    <FundboxFuseWidget
      email={email}
      partnerId={partnerId}
      partnerUserId={businessEntityUuid}
      phoneNumber={phone}
    />
  );
}

FundboxFuseWidgetForAll.propTypes = {
  partnerId: PropTypes.string.isRequired
};

// Component for when feature flag is OFF (show only to preselected)
function FundboxFuseWidgetForPreselected({ partnerId }) {
  const lendingProspect = useLendingProspects();
  const prospectData = lendingProspect?.data?.lendingProspects?.data[0] || {};
  const {
    isPreSelected: isPreselected = false,
    email = null,
    businessEntityUuid = null,
    phone = null
  } = prospectData;

  if (!isPreselected) {
    return null;
  }

  return (
    <FundboxFuseWidget
      email={email}
      partnerId={partnerId}
      partnerUserId={businessEntityUuid}
      phoneNumber={phone}
    />
  );
}

FundboxFuseWidgetForPreselected.propTypes = {
  partnerId: PropTypes.string.isRequired
};

// Container component that decides which version to render
function FundboxFuseWidgetContainer({ isEnableFundboxQualifiedListControlWidget, partnerId }) {
  if (isEnableFundboxQualifiedListControlWidget) {
    return <FundboxFuseWidgetForAll partnerId={partnerId} />;
  }

  return <FundboxFuseWidgetForPreselected partnerId={partnerId} />;
}

FundboxFuseWidgetContainer.propTypes = {
  isEnableFundboxQualifiedListControlWidget: PropTypes.bool.isRequired,
  partnerId: PropTypes.string.isRequired
};

export default function Dashboard() {
  const { FUNDBOX_PARTNER_ID } = useEnv('head-first');
  const { isOpeningModal } = useCheckOpenModal();
  const { isDisable } = useDisableDashboardContext();
  const isNotOpeningModal = !isOpeningModal;
  const { pathname } = useLocation();
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  const { appStatus } = useApplicationStatusContext();
  useQueryParamModal();

  // Feature flag to control whether to show FundboxFuseWidget to all users or only preselected ones
  const [isEnableFundboxQualifiedListControlWidget, isLoadingFlag] = useFeatureFlag(
    ENABLE_FUNDBOX_QUALIFIED_LIST_CONTROL_WIDGET
  );

  // Next Up Cards point users toward banking actions that no longer apply once banking is sunsetting
  const [isBankingSunsetPh3] = useFeatureFlag(ZB_BANKING_SUNSET_PH3);

  const banners = [
    BankingTransitionBanner(),
    ServerDrivenBanner(),
    FrozenAccountBanner(),
    UnAuthenticatedBanner(),
    CancelSubscriptionBanner(),
    ApplicationStatusBanner(),
    ApplicationDocsStatusBanner(),
    SuspectedFraudCardBanner(),
    FrozenCardBanner(),
    ActivateCardBanner(),
    AdditionalInfoBanner()
  ];

  const properties = useMemo(
    () => ({
      customDestinationPageName_: PAGE_TYPE_MAP_APP_STATUS[appStatus],
      appPageName: PAGE_TYPE_MAP_APP_STATUS[appStatus],
      appSection: 'banking',
      experienceOrigin: 'dashboard',
      dynamicProperties: {
        pageType: PAGE_TYPE_MAP_APP_STATUS[appStatus]
      }
    }),
    [appStatus]
  );

  return (
    <DashboardWrapperStyled
      aria-label="Dashboard wrapper"
      isDisplay={isNotOpeningModal}
    >
      <HubContainerGridStyled marginTop={{ xs: '32px!important', md: '48px!important' }}>
        <Notifications banners={banners} />
        {isNotOpeningModal && appStatus && (
          <PageViewedTracker
            {...properties}
            key={pathname}
          />
        )}
        {!isWebView && (
          <BreadcrumbWrapStyled>
            <Breadcrumbs
              items={[
                {
                  to: '../../finances',
                  label: 'Finances'
                },
                {
                  label: 'Business Banking'
                }
              ]}
            />
          </BreadcrumbWrapStyled>
        )}
        <Stack position="relative">
          {/** Show layer when user opening the modal */}
          {isDisable && <DisableLayer aria-label="Disable layer" />}
          <BankingTitle />
          <FocusGrid>
            <LeftSideStyled>
              <BalanceBox />
              {isMobile && (
                <Box marginTop="16px">
                  {!isBankingSunsetPh3 && <NextUpCards />}
                  <WhatsNewBanner />
                </Box>
              )}
              <Transactions />
            </LeftSideStyled>
            <RightSideStyled>
              {!isMobile && (
                <>
                  {!isBankingSunsetPh3 && <NextUpCards />}
                  <WhatsNewBanner />
                </>
              )}
              {/* Only render the container when feature flag has finished loading */}
              {!isLoadingFlag && (
                <FundboxFuseWidgetContainer
                  isEnableFundboxQualifiedListControlWidget={
                    isEnableFundboxQualifiedListControlWidget
                  }
                  partnerId={FUNDBOX_PARTNER_ID}
                />
              )}
              <FinanceTrends />
              <BankingChart />
              <ConnectedAccounts />
            </RightSideStyled>
          </FocusGrid>
          <Footer />
        </Stack>
      </HubContainerGridStyled>
    </DashboardWrapperStyled>
  );
}
